

import React, { forwardRef } from 'react'

import { InfoIcon, CheckIcon, TriangleAlertIcon, XIcon } from 'lucide-react'

import { Alert as MuiAlert, AlertProps as MuiAlertProps, AlertTitle, Typography, Stack, IconButton, Box } from '@mui/material'
import { styled, alpha } from '@mui/material/styles';
import type { SxProps } from '@mui/system'

type AlertSeverity = 'success' | 'info' | 'warning' | 'error'
type AlertVariant = 'filled' | 'outlined'

const StyledMuiAlert = styled(MuiAlert)<MuiAlertProps>(({ theme }) => ({
  // VERA-790: roomier padding (12px/16px) to match the subtle inline-alert reference
  // (EncodePackagePage's local `InlineAlert`) — shared across all variants (filled,
  // outlined, neutral), same as the previous flat padding was.
  // 8px, matching the field Input's radius (packages/ui/.../Input/Input.tsx). Alerts sit
  // directly above and below inputs in forms and modals, and at 6px they were rounded on
  // a different curve to the field they related to — close enough to look like a mistake
  // rather than a distinction. One radius for both, so a stack of them reads as one set
  // of surfaces. This is now the ONLY radius — the toast-only 12px override was removed
  // (see the `neutral` block below for why).
  borderRadius: 8,
  // VERA-304: absolute px, NOT theme.spacing(). This is a SHARED component consumed
  // by both apps/platform (4px spacing base) and apps/app (MUI's default 8px base) —
  // `theme.spacing(3) theme.spacing(4)` silently doubled to 24px/32px in apps/app.
  // Unlike a per-call-site `spacing={n}` there is no "convert per app" available
  // inside a shared component's own styled() block, so a literal px value is the
  // only value that reads the same regardless of which app's theme it's rendered under.
  // VRG-607 — right padding 16 -> 12. The close X is an icon button with its own
  // hit area, so it already carries visual breathing room on that side; a full 16
  // beyond it read as a gap rather than as margin. Left stays 16, where the icon
  // needs the inset.
  padding: '12px 12px 12px 16px',
  alignItems: 'center',
  // Absolute px for the same reason as the padding above — every theme.spacing()
  // value in this shared component renders DOUBLE in apps/app (8px MUI base) vs
  // apps/platform (@repo/ui's 4px base). Measured: this gap came out 16px instead
  // of 8px, which (with the icon margin below) squeezed the message column enough
  // to wrap the text several lines early.
  gap: '8px',
  position: 'relative',
  boxShadow: 'none',
  '& .MuiAlert-icon': {
    marginRight: '8px',
    padding: 0,
    alignItems: 'center',
    display: 'flex',
  },
  '& .MuiAlert-message': {
    padding: 0,
    width: '100%',
  },
}));

type Variation = {
  background: string
  backgroundImage?: string
  border: string
  titleColor: string
  descriptionColor: string
  iconColor: string
  buttonColor: string
  closeColor: string
}

const severityTokens: Record<AlertSeverity, Record<AlertVariant, Variation>> = {

  // TODO: use the theme library instead of hardcodeing color!!! 
  error: {
    filled: {
      background: '#dc2626',
      border: '#dc2626',
      titleColor: '#ffffff',
      descriptionColor: 'rgba(255,255,255,0.72)',
      iconColor: '#ffffff',
      buttonColor: '#ffffff',
      closeColor: '#ffffff',
    },
    outlined: {
      background: '#fef2f2',
      border: '#fee2e2',
      titleColor: '#0f172a',
      descriptionColor: 'rgba(15,23,42,0.72)',
      iconColor: '#0f172a',
      buttonColor: '#0f172a',
      closeColor: '#0f172a',
    },
  },
  success: {
    filled: {
      background: '#16a34a',
      border: '#16a34a',
      titleColor: '#ffffff',
      descriptionColor: 'rgba(255,255,255,0.72)',
      iconColor: '#ffffff',
      buttonColor: '#ffffff',
      closeColor: '#ffffff',
    },
    outlined: {
      background: '#f0fdf5',
      border: 'rgba(0,0,0,0.06)',
      titleColor: '#0f172a',
      descriptionColor: 'rgba(15,23,42,0.72)',
      iconColor: '#0f172a',
      buttonColor: '#0f172a',
      closeColor: '#0f172a',
    },
  },
  warning: {
    filled: {
      background: '#ea670c',
      border: '#ea670c',
      titleColor: '#ffffff',
      descriptionColor: 'rgba(255,255,255,0.72)',
      iconColor: '#ffffff',
      buttonColor: '#ffffff',
      closeColor: '#ffffff',
    },
    outlined: {
      // Faded amber that reads on dark + light surfaces (translucent fill so it
      // tints whatever's behind it rather than a bright near-white box).
      background: 'rgba(245, 158, 11, 0.12)',
      border: 'rgba(245, 158, 11, 0.35)',
      titleColor: '#fbbf24',
      descriptionColor: 'rgba(252, 211, 77, 0.9)',
      iconColor: '#f59e0b',
      buttonColor: '#fbbf24',
      closeColor: '#fbbf24',
    },
  },
  info: {
    filled: {
      background: '#585b62',
      border: '#585b62',
      titleColor: '#ffffff',
      descriptionColor: 'rgba(255,255,255,0.72)',
      iconColor: '#ffffff',
      buttonColor: '#ffffff',
      closeColor: '#ffffff',
    },
    outlined: {
      background: '#f4f4f5',
      border: '#e4e4e7',
      titleColor: '#0f172a',
      descriptionColor: 'rgba(15,23,42,0.72)',
      iconColor: '#0f172a',
      buttonColor: '#0f172a',
      closeColor: '#0f172a',
    },
  },
}

// VERA-360 (VERA-361): the outlined variant is computed from the resolved color mode
// instead of the hardcoded light-only tokens above, so toasts read on BOTH light and dark.
// Tinted look: a mode-aware surface + a translucent severity-tint overlay + mode-aware
// severity text. The solid `filled` tokens above are still used as-is.
// NOTE: the snackbar mounts ABOVE the app's MUI ThemeProvider, so useTheme() here returns
// the default light theme — we resolve dark mode from the <html> color-scheme that
// VeraThemeProvider keeps in sync instead.
const TINT_HUE: Record<AlertSeverity, string> = {
  success: '#16a34a',
  error: '#dc2626',
  warning: '#f59e0b',
  info: '#71717a',
}

const TINT_TEXT: Record<AlertSeverity, { light: string; dark: string }> = {
  success: { light: '#15803d', dark: '#4ade80' },
  error: { light: '#b91c1c', dark: '#f87171' },
  warning: { light: '#b45309', dark: '#fbbf24' },
  info: { light: '#3f3f46', dark: '#d4d4d8' },
}

// Light surface is grey[50] (not pure white) — the page itself is pure white, so an
// identical toast surface had zero color separation and relied entirely on the shadow
// + an 8%-opacity border to read as a distinct card.
const TOAST_SURFACE = { light: '#f6f6f6', dark: '#141417' }

// Toast-only icon override: the inline-banner `info` icon (TINT_HUE.info,
// '#71717a') reads fine tinted against a card, but on a toast it looked like
// muddy grey next to the crisp near-white title text. Brighter, closer to the
// title color, without literally being white (keeps it a hair muted vs. the
// title so it doesn't outshine it).
// VRG-607 — LIGHT mode uses the darker TINT_TEXT values, not TINT_HUE.
//
// TINT_HUE is tuned to sit on a TINTED surface; on the near-white neutral toast
// (#f6f6f6) the same hues wash out. Measured against that surface, with WCAG's
// 3.0:1 floor for graphical objects:
//
//   warning  #f59e0b  1.99:1  <- FAILED outright
//   success  #16a34a  3.05:1  <- scraped the floor; read as faint
//   error    #dc2626  4.47:1
//   info     #71717a  4.47:1
//
// Amber on near-white is the classic failure, and it was the severity carrying
// "heads up" — the one a reader most needs to catch. The TINT_TEXT light values
// are already the darkened-for-light-surfaces variants of the same hues, so this
// reuses them rather than inventing a third set: 4.65 / 4.64 / 5.99 / 9.66:1.
//
// DARK mode is unchanged. On #141417 the mid hues are the legible choice, and
// TINT_TEXT's dark values are pastels tuned for tinted surfaces — swapping those
// in would make the glyph outshine the title.
// VRG-607 — BRIGHT hues kept, with weight doing the legibility work (strokeWidth
// 1 -> 1.75 at the render site). Only warning is adjusted, and only as far as it
// has to be.
//
// Stroke weight and contrast ratio are not interchangeable: a thicker stroke puts
// more pixels of the hue on screen, which genuinely reads stronger, but the
// measured ratio is a property of the two colours and does not move. So weight
// fixes "faint", and only a darker hue fixes "below the floor".
//
// Measured against the light toast surface (#f6f6f6), WCAG floor 3.0:1 for a
// graphical object:
//
//   success  #16a34a  3.05:1  passes
//   error    #dc2626  4.47:1  passes
//   info     #71717a  4.47:1  passes
//   warning  #f59e0b  1.99:1  BELOW the floor — knowingly kept, see below
//
// ⚠️ WARNING IS A DELIBERATE EXCEPTION, decided by the product owner, not an
// oversight to "fix".
//
// Darkened candidates were tried and rejected on sight: #b45309 (4.65:1) and
// #c2740a (3.35:1) both clear the floor and both read BROWN rather than yellow.
// The call was to match `#f59e0b` — the same amber the Pending/Invited status
// chips use — so the warning glyph belongs to the product's existing yellow
// instead of being a one-off darker tone nothing else shares.
//
// Why the chip gets away with it and the toast does not: the chip paints that
// amber on a pale amber BACKGROUND, so its local contrast is fine. The toast
// surface is near-white, so the identical hue measures 1.99:1 on it.
//
// What carries the meaning instead: the title text ("Warning"), the body copy,
// and strokeWidth 1.75 at the render site, which puts noticeably more pixels of
// the hue on screen. WCAG 1.4.11 governs graphics "required to understand the
// content", and here the sentence is what says it — the glyph reinforces. That
// is the argument this rests on; it is a judgement, not a loophole, and it is
// written down so the next reader can re-open it rather than guess.
//
// DARK mode is unchanged: on #141417 the mid hues are already legible.
const NEUTRAL_ICON_COLOR: Record<AlertSeverity, { light: string; dark: string }> = {
  success: { light: TINT_HUE.success, dark: TINT_HUE.success },
  error: { light: TINT_HUE.error, dark: TINT_HUE.error },
  warning: { light: TINT_HUE.warning, dark: TINT_HUE.warning },
  info: { light: '#71717a', dark: '#d4d4d8' },
}

const isDarkMode = (): boolean => {
  if (typeof document === 'undefined') return false
  return getComputedStyle(document.documentElement).colorScheme.includes('dark')
}

// `isDarkMode()` reads `<html>`'s color-scheme directly (this component mounts
// above the app's MUI ThemeProvider, so `useTheme()`/`useColorMode()` aren't
// available here) — but a plain read is only fresh AT RENDER TIME. A toast that
// stays open (persist) across a light/dark toggle never re-renders for any
// OTHER reason, so it silently keeps showing whatever mode was active when it
// mounted. This observes VeraThemeProvider's `document.documentElement.style`
// write and forces a re-render so the colors actually follow the toggle.
const useIsDarkMode = (): boolean => {
  const [isDark, setIsDark] = React.useState(isDarkMode)
  React.useEffect(() => {
    if (typeof document === 'undefined') return
    const observer = new MutationObserver(() => setIsDark(isDarkMode()))
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] })
    return () => observer.disconnect()
  }, [])
  return isDark
}

// VERA-790: promoted from the EncodePackagePage `InlineAlert` reference — a SUBTLE inline
// banner: thin severity-tinted border over a very low-alpha severity TINT. The icon and
// title carry the severity colour; the description stays a muted NEUTRAL tone (not
// severity-tinted) so it reads as quiet supporting copy rather than a colour-flooded box.
//
// The tint was previously `transparent` in both modes — border only. That read as
// under-defined on a light surface in particular: a thin 28%-alpha outline on white is
// nearly the same object as any other bordered row, so an alert sitting among fields or
// cards did not separate from them. Dark mode LOOKED like it had a tint and did not; it
// just carries a stronger border (40% vs 28%) and coloured text on a dark ground, which
// reads warmer. Giving both modes a real fill is what makes them actually match.
//
// Kept deliberately low (6% light / 10% dark). Enough that the alert is its own surface;
// not so much that it becomes the flooded box the border-only version was reacting to.
const NEUTRAL_TEXT = { light: '#0f172a', dark: '#fafafa' }

const getTintedTokens = (severity: AlertSeverity): Variation => {
  const isDark = isDarkMode()
  const hue = TINT_HUE[severity]
  const text = isDark ? TINT_TEXT[severity].dark : TINT_TEXT[severity].light
  const neutralText = isDark ? NEUTRAL_TEXT.dark : NEUTRAL_TEXT.light
  return {
    // Light mode: a near-white page gives the thin severity border almost no
    // contrast to stand out against, so the alert reads as barely-there. A slight
    // tint fixes that. Dark mode's border already reads with much more contrast
    // against the near-black page, so it only needs a hint of tint, not none.
    background: alpha(hue, isDark ? 0.04 : 0.06),
    backgroundImage: undefined,
    border: alpha(hue, isDark ? 0.4 : 0.28),
    titleColor: text,
    descriptionColor: alpha(neutralText, 0.6),
    iconColor: isDark ? text : hue,
    buttonColor: text,
    closeColor: text,
  }
}

// Sonner/shadcn-style TOAST tokens (opt-in via `neutral`): an editorial surface with
// primary-text title and muted description, rather than a colour-flooded card. Inline
// Alert banners use `getTintedTokens` above — only the snackbar opts in here.
//
// VALENCE (added on request): the surface is no longer neutral regardless of outcome. A
// toast now carries its severity as a 1px hue border over a low-alpha tint of the same
// hue — the SAME treatment, at the SAME alphas, that `getTintedTokens` gives inline
// alerts. The codebase already uses border+tint to mean valence inline; a toast carrying
// the same message class in plain white taught a second visual language for one meaning,
// and colour is the fastest pre-attentive channel for something this transient.
//
// INFO IS DELIBERATELY EXEMPT and keeps the neutral border with no tint. Its hue is grey
// (TINT_HUE.info), so "tinting" it would convey no valence at all — it would just darken
// the border. Keeping info as the quiet baseline is also what preserves the point of the
// exercise: success/error/warning only read as signals because the default does not.
//
// The tint is a background LAYER, not a background colour. A toast floats over page
// content, so a translucent `background` would let text bleed through it; compositing
// `alpha(hue, …)` as a flat gradient over the opaque TOAST_SURFACE gets the tint while
// the card stays opaque.
const getNeutralTokens = (severity: AlertSeverity): Variation => {
  const isDark = isDarkMode()
  const titleColor = isDark ? '#fafafa' : '#0f172a'
  // VRG-607 — tint ERROR only. Was `severity !== 'info'`, i.e. everything except
  // info carried a tinted surface.
  //
  // The pre-attentive argument for tint (see the note below on why severity keeps
  // a colour channel at all) is strongest exactly where MISSING the message costs
  // something. A missed "Scan destination updated" costs nothing — the state
  // visibly changed, which is why the repo's own toast policy says a visible
  // action needs no toast at all. A missed payment failure costs a customer.
  //
  // Spending the tint on all four severities also spent its meaning: when success,
  // warning and error are each a tinted card, "tinted" stops signalling anything
  // and only the hue distinguishes them — which is the slower channel, not the
  // fast one. Reserving it makes red mean something again.
  //
  // Success/warning/info keep the neutral surface and their coloured GLYPH, so
  // they still carry a colour channel; it is just proportionate to the stakes.
  const toned = severity === 'error'
  const hue = TINT_HUE[severity]
  // Same alphas as getTintedTokens — matching the inline banners is the whole point.
  const tint = alpha(hue, isDark ? 0.1 : 0.06)
  return {
    background: isDark ? TOAST_SURFACE.dark : TOAST_SURFACE.light,
    backgroundImage: toned ? `linear-gradient(${tint}, ${tint})` : undefined,
    border: toned
      ? alpha(hue, isDark ? 0.4 : 0.28)
      : isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.08)',
    titleColor,
    // Light mode's alpha-derived grey (0.6 over a near-white surface) measured 4.70:1 —
    // a hair over WCAG AA's 4.5:1 floor for small text, which reads as genuinely faint.
    // Dark mode's 0.6 measures ~6.88:1 (fine as-is), so only light needs the bump.
    descriptionColor: alpha(titleColor, isDark ? 0.6 : 0.75),
    iconColor: isDark ? NEUTRAL_ICON_COLOR[severity].dark : NEUTRAL_ICON_COLOR[severity].light,
    buttonColor: alpha(titleColor, 0.78),
    closeColor: alpha(titleColor, 0.5),
  }
}

interface AlertProps {
  children?: React.ReactNode
  severity?: AlertSeverity
  variant?: AlertVariant
  title?: string
  icon?: React.ReactNode
  button?: React.ReactNode
  // ReactNode, not string: a consequence line often has to emphasise the ONE value the
  // reader is being asked to check — a tag uid, an amount, a domain — and burying it in
  // flat prose is what makes people skim a warning they should be reading.
  description?: React.ReactNode
  onClose?: () => void
  // Sonner-style neutral toast look (neutral surface + colour only on the icon).
  // Opt-in — inline banners stay severity-tinted.
  neutral?: boolean
  sx?: SxProps
}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(({
  title,
  description,
  children,
  severity = 'info',
  icon,
  variant = 'outlined',
  onClose,
  button,
  neutral = false,
  sx,
}, ref) => {
  // Forces a re-render (so the isDarkMode() reads below stay fresh) whenever
  // VeraThemeProvider flips <html>'s color-scheme — see useIsDarkMode above.
  const isDark = useIsDarkMode()
  const tokens = variant === 'filled'
    ? severityTokens[severity].filled
    : neutral
      ? getNeutralTokens(severity)
      : getTintedTokens(severity)

  // ANIMATED DISMISS. `onClose` unmounts the alert at the call site, so clicking X
  // used to make it vanish between two frames — the eye reads that as a glitch
  // rather than as "you closed it", and on a tall page the content below jumps up
  // with no indication why. The alert now collapses its own height and fades before
  // telling the parent, so `onClose` fires only once it is visually gone and the
  // reflow is something you watched happen.
  //
  // Done here rather than at each call site so every dismissible alert gets it for
  // free — and because only this component knows its own rendered height, which the
  // collapse needs. WAAPI over a CSS transition for exactly that reason: height
  // animates from a measured px value, with no max-height guess to tune.
  const innerRef = React.useRef<HTMLDivElement | null>(null)
  const setRefs = React.useCallback((node: HTMLDivElement | null) => {
    innerRef.current = node
    if (typeof ref === 'function') ref(node)
    else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node
  }, [ref])

  const handleDismiss = React.useCallback(() => {
    const el = innerRef.current
    const reduced = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    // No element, no WAAPI, or the user asked for less motion → close immediately.
    if (!el || reduced || typeof el.animate !== 'function') {
      onClose?.()
      return
    }
    // `offsetHeight`, NOT `getBoundingClientRect().height`: the keyframe below is a CSS
    // length on this element, so it must be in the element's own LAYOUT px. Under the
    // platform's text zoom (`zoom` on `#app`) the rect reports PAINTED px (× z), and the
    // collapse visibly jumped to z × its height on its first frame. The padding/margin
    // values read from `getComputedStyle` alongside it are layout px too.
    const height = el.offsetHeight
    const cs = window.getComputedStyle(el)
    el.style.overflow = 'hidden'
    const anim = el.animate(
      [
        { height: `${height}px`, opacity: 1, marginTop: cs.marginTop, marginBottom: cs.marginBottom,
          paddingTop: cs.paddingTop, paddingBottom: cs.paddingBottom },
        { height: '0px', opacity: 0, marginTop: '0px', marginBottom: '0px',
          paddingTop: '0px', paddingBottom: '0px' },
      ],
      { duration: 220, easing: 'cubic-bezier(0.4, 0, 0.2, 1)', fill: 'forwards' },
    )
    // Closing must NOT depend on the animation finishing. An animation's timeline is
    // frozen while its tab is backgrounded, so `onfinish` can be arbitrarily late or
    // never arrive: click X, switch tabs, and the alert is stranded mid-collapse with
    // overflow:hidden — dismissed as far as the user is concerned, still mounted in
    // fact. (Caught exactly that way: in a hidden pane, document.timeline.currentTime
    // sat at 0 and the alert never closed.)
    //
    // So the close is owned by a timer armed in the same breath as the animation, and
    // the animation callbacks merely get there sooner. `settle` runs once whichever
    // path wins.
    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      window.clearTimeout(guard)
      onClose?.()
    }
    const guard = window.setTimeout(settle, 400)
    anim.onfinish = settle
    anim.oncancel = settle
  }, [onClose])

  // VRG-512: this used to be `position: 'absolute'` (top/right pinned against the
  // outer alert box), and the content Stack reserved a flat `pr: 8` (32px) to avoid
  // it. That reservation was sized for the close (X) button — 32x32 — not for a real
  // `button` prop, whose rendered width ("Clear sample data" et al.) routinely exceeds
  // 32px. The description text then wrapped UNDER the absolutely-positioned button
  // instead of around it, because nothing in the layout actually knew the button's
  // width. Fixed at the layer-1 source (§47) rather than per-caller: any Alert with
  // both a `description` and a `button` had this defect, and a per-call-site padding
  // guess would just be the same wrong idea copied to each caller.
  //
  // The fix is to stop guessing a width and let flexbox own it: `action` renders as an
  // ordinary flex-row sibling of the content Stack (below), with `flexShrink: 0` so it
  // keeps its natural width, while the content Stack keeps `flex: 1, minWidth: 0` so it
  // wraps to whatever room is actually left — the real button width, not a placeholder.
  //
  // Orthogonal to, and compatible with, the type-size change immediately below: that one
  // decides how large the text is, this one decides how much room it gets to wrap in.

  // ONE size for the alert's text, title and description alike — 16px inline, 14px on
  // the compact `neutral` toast. The fork is the title's own, pre-existing one; the
  // description now simply tracks it instead of sitting a step below.
  //
  // WHY THE DESCRIPTION WENT UP (it was `variant="body3"`, 14px): most alerts in this
  // codebase have NO title — `ConfirmSettingModal` and `SendToLocationModal` both pass
  // `description` alone, so it is not subordinate supporting copy, it IS the message.
  // At 14px it rendered SMALLER than the plain `body2` preamble prose immediately above
  // it in that same modal, which inverted the real hierarchy: the sentence stating what
  // the change costs was the least legible thing in the dialog.
  //
  // WHY EQUAL TO THE TITLE IS NOT AN INVERSION: subordination is still carried by weight
  // (title 500/600 vs body 400) and by colour (title at full contrast in the severity
  // hue; description at a muted 60–75% neutral). That is exactly how the `neutral` toast
  // has always worked — title and description both 14px, differing only in weight and
  // colour — so equal size with weight+colour separation is this component's own shipped
  // precedent, not a new idea. Tracking the fork also keeps the description from ever
  // exceeding the title, which a flat 16px WOULD have done on the 14px-titled toast.
  //
  // AN EXPLICIT fontSize, NOT `variant="body2"`: the two apps' type scales disagree.
  // `body2` is 16px under @repo/ui (apps/platform) but 14px under apps/app's own theme
  // (both `body2` and `body3` resolve to `font.label` = 14 there), so the variant swap
  // would have been a silent no-op in apps/app. A literal px is the only value that
  // reads the same in both — the same reasoning as the padding/gap above (§47).
  //
  // `lineHeight` is unitless (1.5, §15) rather than body3's absolute 18px, which at 16px
  // would have been a cramped 1.125 for the multi-line prose these descriptions carry.
  const textSize = neutral ? 14 : 16

  const action = button || onClose
    ? (
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{
          color: tokens.buttonColor,
          flexShrink: 0,
        }}
      >
        {button ? (
          <Box
            sx={{
              color: tokens.buttonColor,
              fontSize: 16,
              lineHeight: 1.5,
              textDecoration: typeof button === 'string' ? 'underline' : 'none',
              '& *': { color: tokens.buttonColor },
            }}
          >
            {button}
          </Box>
        ) : null}
        {onClose && (
          <IconButton
            size="small"
            disableRipple
            onClick={handleDismiss}
            sx={{
              color: tokens.closeColor,
              width: 32,
              height: 32,
              '&:hover': { opacity: 0.8, backgroundColor: 'transparent' },
            }}
          >
            <XIcon strokeWidth={1} size={20} />
          </IconButton>
        )}
      </Stack>
    )
    : undefined

  return (
    <StyledMuiAlert
      ref={setRefs}
      variant="standard"
      sx={{
        // VERA-304: alerts ARRIVE, they don't blink into existence. An alert is
        // almost always the consequence of something the user just did, and a
        // static appearance gives the eye nothing to follow — it reads as "this
        // was always here / the page re-rendered" rather than "this is the answer
        // to your click". A short scale-up carries attention to it.
        //
        // Mount only, and it lives HERE rather than at call sites so every alert
        // in both apps animates identically — the close collapse below was already
        // owned by this component and the entrance belongs beside it.
        //
        // `cubic-bezier(.34,1.56,.64,1)` overshoots slightly — the same curve the
        // scan-success glyph uses, so the two read as one motion vocabulary. That
        // overshoot IS the "pop"; a plain ease-out at this distance reads as a
        // fade, not an arrival.
        //
        // fill `backwards` (NOT `both`): it applies the from-state before the first
        // frame so there is no opacity-1 flash, but does NOT persist the end state
        // afterwards. That matters because the DISMISS path animates opacity/height
        // through the Web Animations API on this same element — leaving a CSS fill
        // pinned on opacity is exactly the kind of thing that makes a close look
        // stuck.
        '@keyframes veraAlertIn': {
          from: { opacity: 0, transform: 'scale(0.96) translateY(-4px)' },
          to: { opacity: 1, transform: 'none' },
        },
        animation: 'veraAlertIn 200ms cubic-bezier(.34,1.56,.64,1) backwards',
        // Motion here is decorative — the alert's meaning is fully carried by its
        // text, icon and colour — so it is switched off outright rather than
        // shortened for anyone who has asked for reduced motion.
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
        color: tokens.titleColor,
        backgroundColor: tokens.background,
        backgroundImage: tokens.backgroundImage ?? 'none',
        border: `1px solid ${tokens.border}`,
        ...(neutral && {
          // Dark mode: a light-on-dark shadow barely registers against an
          // already-near-black page — bump the shadow's own darkness/blur AND
          // add a faint white ring, so the toast reads as lifted rather than
          // flat against the backdrop. Light mode's shadow already has contrast
          // to work with, so it's unchanged.
          boxShadow: isDark
            ? '0 12px 32px -6px rgba(0,0,0,0.65), 0 2px 10px -2px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.06)'
            : '0 6px 16px -4px rgba(0,0,0,0.18), 0 2px 6px -2px rgba(0,0,0,0.12)',
          // NO radius override. This used to force 12px "because toasts float", but
          // `neutral` is not only used by floating toasts — in-modal notes opt into
          // the same neutral look (see InstallationHandleModal's MODAL_NOTE_SX, which
          // cancels the shadow but could not cancel this), so an inline note sitting
          // right under a text field was rounded 12px against the field's 8px. Every
          // alert now uses the single 8px in StyledMuiAlert, matching Input.
          // Trade-off accepted: floating toasts no longer match InfoBlock/Table at
          // 12px. Matching the field an alert sits beside matters more than matching
          // a card it never appears next to.
        }),
        '& .MuiAlert-icon': {
          color: tokens.iconColor,
        },
        '& .MuiAlert-message': {
          color: tokens.titleColor,
        },
        ...sx,
      }}
      severity={severity}
      iconMapping={{
        // VRG-607 — strokeWidth 1 -> 1.75. lucide's 1px stroke at this size reads as
        // faint on a near-white surface, and weight is the right lever for that: it
        // adds presence without shifting the hue, which is what let the bright colours
        // be KEPT rather than darkened into mud. Contrast ratio is a property of the
        // two colours and does not move with weight, so the two changes solve
        // different halves of it — see NEUTRAL_ICON_COLOR.
        success: <CheckIcon fontSize="inherit" strokeWidth={1.75} color={tokens.iconColor} />,
        info: <InfoIcon fontSize="inherit" strokeWidth={1.75} color={tokens.iconColor} />,
        warning: <TriangleAlertIcon fontSize="inherit" strokeWidth={1.75} color={tokens.iconColor} />,
        error: <TriangleAlertIcon fontSize="inherit" strokeWidth={1.75} color={tokens.iconColor} />,
      }}
      icon={icon}
    >
      {/* VRG-512: `action` used to go through MuiAlert's `action` prop while being
          absolutely-positioned in its own JSX (see the comment above `action` — that
          combination is what let the button overlap the description). It's now an
          ordinary flex sibling of the content Stack, in normal flow, so the browser's
          own flex/wrap math (not a guessed `pr`) decides how much room the text gets. */}
      {/* VRG-607 — alignment depends on whether there IS a second line.
          `flex-start` is right for a title + description: the action belongs beside
          the TITLE, not floating against the middle of a two-line block.
          It is wrong for a title on its own. The title renders at 15px/1.4 and an
          action like "Report a problem" at 13px, so aligning their TOPS leaves the
          smaller text riding high — while the icon centres itself on the message
          box, so the row ends up with three things on three different baselines.
          A single-line toast centres instead, which is what makes icon, title and
          action read as one line.
          Title-only toasts became common in this ticket: the provider now drops a
          body that would merely repeat the title, so this case went from rare to
          routine and took the misalignment with it. */}
      <Stack
        spacing={6}
        justifyContent="space-between"
        alignItems={description || children ? 'flex-start' : 'center'}
        direction="row"
      >
        <Stack
          spacing={0.5}
          sx={{
            flex: 1,
            minWidth: 0,
          }}
        >
          {title && (
            <AlertTitle sx={{ fontSize: textSize, fontWeight: neutral ? 500 : undefined, lineHeight: 1.4, mb: 0, color: tokens.titleColor }}>
              {title}
            </AlertTitle>
          )}
          {description && (
            <Typography sx={{ fontSize: textSize, lineHeight: 1.5, color: tokens.descriptionColor }}>
              {description}
            </Typography>
          )}
          {children}
        </Stack>
        {action}
      </Stack>
    </StyledMuiAlert>
  )
})

Alert.displayName = 'Alert'
